import type { TDocumentDefinitions, Content, TableCell } from "pdfmake/interfaces.js";
import type { DocumentBuilder, DocumentSnapshot } from "../document-engine.js";
import { documentEngine } from "../document-engine.js";
import type { GeneratedLineList } from "../../../domain/inspection-book/line-list.js";
import { formatReportValue } from "../../../shared/format/report-value.js";
import type { DocumentColors, DocumentVisual } from "../../../domain/studio/visual-design.js";
import {
  buildLineListTable,
  buildLineListReferencesBlock,
  buildLineListFailureBlock,
  resolveLineListLayout,
  lineListPageMargins,
  LINE_LIST_PAGE_SIZE,
  LINE_LIST_PAGE_ORIENTATION
} from "./line-list-render.js";
import type { LineListLayoutResult } from "./line-list-layout.js";

/** Margens/tamanho do resto do Book (A4 retrato) — usados para reverter ao sair da seção A3 da Lista de Linhas. */
const BOOK_PAGE_SIZE = "A4" as const;
const BOOK_PAGE_ORIENTATION = "portrait" as const;
const BOOK_PAGE_MARGINS: [number, number, number, number] = [36, 60, 36, 45];

/**
 * STUDIO INSPECT IA — paleta fixa de sempre, usada como FALLBACK quando não
 * há visual carimbado/publicado (Book anterior ao Studio ou provedor não
 * ligado): o PDF sai byte-a-byte como antes desta mudança. Cores SEMÂNTICAS
 * (válido/pendente/bloqueador — verde/âmbar/vermelho/cinza dos badges de
 * status) NÃO entram aqui de propósito: eu não mudam com o tema, do mesmo
 * jeito que o `LAUDO_FILL` do kit de PM/US nunca muda com `activeVisual`.
 */
const BOOK_COLORS = {
  ink: "#0F172A",
  accent: "#2563EB",
  sectionBandFill: "#F8FAFC",
  sectionBandText: "#1E293B",
  tableHeaderFill: "#F1F5F9",
  tableHeaderText: "#0F172A",
  border: "#CBD5E1",
  gridBorder: "#E2E8F0",
  footerLine: "#94A3B8"
};

/**
 * STUDIO INSPECT IA — visual do Book em montagem (mesmo padrão do
 * `activeVisual`/`kitColor` de `ndt-report-kit.ts`, replicado aqui porque o
 * Book é uma família de documento própria). Vale só DURANTE `build()` e é
 * sempre restaurado no `finally` — nenhum outro documento herda o valor.
 */
let activeVisual: DocumentVisual | null = null;

const bookColor = (key: keyof DocumentColors, fallback: string): string => activeVisual?.colors[key] ?? fallback;

/** Uma seção do Book: o índice e o corpo saem daqui, com a mesma numeração. */
interface BookPdfSection {
  title: string;
  indexDescription: string;
  indexStatus: string;
  indexColor?: string;
  body: Content[];
}

export class InspectionBookBuilder implements DocumentBuilder {
  build(snapshot: DocumentSnapshot): TDocumentDefinitions {
    // STUDIO INSPECT IA — vale só durante esta montagem (restaurado no
    // finally), mesmo padrão de `withDocumentVisual` em ndt-report-kit.ts.
    // Ausente = exatamente o Book de sempre (nenhuma cor muda).
    const visual: DocumentVisual | null = (snapshot.data || {}).visual ?? null;
    const previous = activeVisual;
    activeVisual = visual;
    try {
      return this.compose(snapshot, visual);
    } finally {
      activeVisual = previous;
    }
  }

  private compose(snapshot: DocumentSnapshot, visual: DocumentVisual | null): TDocumentDefinitions {
    const data = snapshot.data || {};
    const book = data.book || {};
    const equipment = data.equipment || {};
    const inspection = data.inspection || {};
    const report = data.report || {};
    const checklist = data.checklist || {};
    const recommendations: any[] = data.recommendations || [];
    const issues: any[] = data.issues || [];
    const items: any[] = book.items || [];
    const generatedLineList: GeneratedLineList | null = data.generatedLineList || null;
    // Autoajuste (comando "autoajuste inteligente"): resolvido uma única vez
    // aqui e reaproveitado tanto para a margem da seção A3 quanto para a
    // tabela — nunca recalculado em dois lugares divergentes.
    const lineListLayout: LineListLayoutResult | null =
      generatedLineList && generatedLineList.totalLines > 0 ? resolveLineListLayout(generatedLineList) : null;

    const isIssued = book.status === 'ISSUED';
    const openBlockers = issues.filter((i: any) => (i.severity === 'BLOCKER' || i.severity === 'CRITICAL') && (i.status === 'OPEN' || !i.status));
    const isPreliminary = !isIssued || openBlockers.length > 0;

    // STUDIO INSPECT IA — logo/texto do cabeçalho: imagem publicada (PNG/JPEG)
    // ou o texto INSPECT IA de sempre, exatamente como o cabeçalho dos
    // demais relatórios (`buildInspectionReportHeader`). A cor do texto
    // (`headerText`) tinge só quando não há logo de imagem.
    const headerTextColor = bookColor('headerText', BOOK_COLORS.ink);
    const brandLogo: Content = visual?.logoImage
      ? ({ image: visual.logoImage, fit: [140, 26] } as Content)
      : { text: visual?.logoText || 'INSPECT IA', style: 'brandTitle', color: headerTextColor };

    return {
      pageSize: 'A4',
      pageOrientation: 'portrait',
      pageMargins: [36, 60, 36, 45],
      watermark: isPreliminary
        ? { text: 'PRELIMINAR / INCOMPLETO', color: '#DC2626', opacity: 0.12, bold: true }
        : undefined,
      header: (currentPage: number, pageCount: number) => {
        if (currentPage === 1) return { text: '' }; // No header on cover
        return {
          margin: [36, 16, 36, 0],
          columns: [
            {
              stack: [
                brandLogo,
                { text: 'Book de Inspeção Técnica & Integridade', style: 'brandSubtitle' }
              ],
              width: '45%'
            },
            {
              stack: [
                { text: `${book.bookNumber || 'BOOK-NR13'} • Rev. ${book.revision || '00'}`, style: 'docNumber', color: bookColor('headerText', BOOK_COLORS.accent), alignment: 'right' },
                { text: `TAG: ${equipment.tag || equipment.name || 'EQUIP-01'}`, style: 'brandSubtitle', alignment: 'right' }
              ],
              width: '55%'
            }
          ]
        };
      },
      footer: (currentPage: number, pageCount: number, pageSize) => {
        // Linha do rodapé precisa acompanhar a largura real da página: a
        // Lista de Linhas troca para PAISAGEM (comando complementar, item
        // 16/17) e uma largura fixa de retrato deixaria a linha curta demais.
        const lineWidth = pageSize.width - 72;
        return {
          margin: [36, 12, 36, 0],
          stack: [
            {
              canvas: [{ type: 'line', x1: 0, y1: 0, x2: lineWidth, y2: 0, lineWidth: 0.5, lineColor: bookColor('border', BOOK_COLORS.footerLine) }],
              margin: [0, 0, 0, 4]
            },
            {
              columns: [
                { text: 'INSPECT IA • Dossiê Técnico Oficial Conforme NR-13', style: 'footerText' },
                { text: `Página ${currentPage} de ${pageCount}`, style: 'footerText', alignment: 'right' }
              ]
            }
          ]
        };
      },
      content: [
        // ----------------------------------------------------
        // PÁGINA 1: CAPA OFICIAL DO BOOK DE INSPEÇÃO
        // ----------------------------------------------------
        this.buildCoverPage(book, equipment, inspection, report, isPreliminary, visual),
        { text: '', pageBreak: 'after' },
        ...this.buildContentWithSections(
          this.sectionsOf({ book, equipment, inspection, report, checklist, recommendations, issues, items, generatedLineList, lineListLayout, isPreliminary }),
          lineListLayout
        )
      ],
      styles: {
        brandTitle: { fontSize: 11, bold: true, color: headerTextColor },
        brandSubtitle: { fontSize: 8, color: '#64748B' },
        docNumber: { fontSize: 10, bold: true },
        footerText: { fontSize: 8, color: '#64748B' },
        coverMainTitle: { fontSize: 22, bold: true, color: BOOK_COLORS.ink, alignment: 'center', margin: [0, 8, 0, 4] },
        coverSubtitle: { fontSize: 11, color: '#475569', alignment: 'center', margin: [0, 0, 0, 16] },
        sectionHeading: { fontSize: 12, bold: true, color: bookColor('sectionTitleText', BOOK_COLORS.sectionBandText), margin: [0, 8, 0, 6] },
        tableHeader: { fontSize: 8, bold: true, color: bookColor('tableHeaderText', BOOK_COLORS.tableHeaderText), fillColor: bookColor('tableHeaderFill', BOOK_COLORS.tableHeaderFill), alignment: 'left' },
        tableCell: { fontSize: 8, color: '#334155' },
        boldCell: { fontSize: 8, bold: true, color: '#0F172A' },
      }
    };
  }

  /**
   * Seções do Book, na ordem oficial. A LISTA DE LINHAS entra aqui só quando
   * o Book é de um Sistema de Tubulação com Linhas — e a numeração das demais
   * seções se ajusta sozinha, porque índice e corpo saem desta mesma lista
   * (nenhum número de seção é fixado no código).
   */
  private sectionsOf(data: {
    book: any; equipment: any; inspection: any; report: any; checklist: any;
    recommendations: any[]; issues: any[]; items: any[]; generatedLineList: GeneratedLineList | null;
    lineListLayout: LineListLayoutResult | null; isPreliminary?: boolean;
  }): BookPdfSection[] {
    const sections: BookPdfSection[] = [
      {
        title: 'MATRIZ DE CONTROLE DOCUMENTAL DO BOOK',
        indexDescription: 'Matriz de Controle Documental do Book',
        indexStatus: 'CONSOLIDADO', indexColor: '#16A34A',
        body: [this.buildDocumentMatrixTable(data.items), { text: '', margin: [0, 10, 0, 0] }]
      },
      {
        // O corpo do índice é injetado em buildSections (precisa da lista pronta).
        title: 'ÍNDICE GERAL DE CONSOLIDAÇÃO',
        indexDescription: 'Índice Geral de Consolidação',
        indexStatus: 'CONSOLIDADO', indexColor: '#16A34A',
        body: []
      },
      {
        title: 'DADOS DA INSPEÇÃO E ESPECIFICAÇÕES DE PROJETO',
        indexDescription: 'Dados Gerais de Identificação e Parâmetros de Projeto NR-13',
        indexStatus: 'CONSOLIDADO', indexColor: '#16A34A',
        body: [this.buildInspectionDataSection(data.equipment, data.inspection, data.report), { text: '', margin: [0, 12, 0, 0] }]
      },
      {
        title: 'RESUMO DO CHECKLIST DE INSPEÇÃO VISUAL (NR-13)',
        indexDescription: 'Checklist de Inspeção Visual (NR-13 item 13.5)',
        indexStatus: 'CONSOLIDADO', indexColor: '#16A34A',
        body: [this.buildChecklistSummarySection(data.checklist), { text: '', pageBreak: 'after' }]
      },
      {
        title: 'RELATÓRIO DE INTEGRIDADE — RI & PARECER TÉCNICO PLH',
        indexDescription: 'Relatório de Integridade (RI) com Parecer do PLH',
        indexStatus: 'CONSOLIDADO', indexColor: '#16A34A',
        body: [this.buildRiSummarySection(data.report, data.equipment), { text: '', margin: [0, 12, 0, 0] }]
      },
      {
        title: 'PLANO DE AÇÃO E RECOMENDAÇÕES DE INSPEÇÃO',
        indexDescription: 'Plano de Ação e Recomendações de Inspeção',
        indexStatus: 'CONSOLIDADO', indexColor: '#16A34A',
        // Quando a Lista de Linhas vem a seguir, ela entra num node `section`
        // próprio (A3 paisagem) — que JÁ força página nova sozinho. Repetir
        // um pageBreak aqui na fronteira quebraria a troca de tamanho de
        // página do pdfmake (verificado: gera a seção inteira em A4 errado).
        body: [this.buildRecommendationsSection(data.recommendations), ...(data.generatedLineList ? [] : [{ text: '', pageBreak: 'after' as const }])]
      }
    ];

    if (data.generatedLineList) {
      sections.push({
        title: 'LISTA DE LINHAS',
        indexDescription: `Lista de Linhas do Sistema de Tubulação (${data.generatedLineList.totalLines} ${data.generatedLineList.totalLines === 1 ? 'linha' : 'linhas'})`,
        indexStatus: 'GERADA AUTOMATICAMENTE', indexColor: '#16A34A',
        // A3 PAISAGEM (comando "A3 sem compressão de texto") — a troca de
        // página/tamanho é feita pelo node `section` em buildContentWithSections,
        // nunca por pageBreak+pageOrientation manual.
        body: this.buildLineListSection(data.generatedLineList, data.lineListLayout, data.book, data.equipment, data.inspection)
      });
    }

    sections.push(
      {
        title: 'DOCUMENTAÇÃO TÉCNICA ANEXA & DESENHOS',
        indexDescription: 'Documentação Técnica Anexa, Desenhos, Listas e Isométricos',
        indexStatus: 'ANEXOS VINCULADOS',
        body: [this.buildAttachedDocsSection(data.items), { text: '', margin: [0, 12, 0, 0] }]
      },
      {
        title: 'VALIDAÇÃO TÉCNICA DO CÓRTEX & ASSINATURAS FORMAIS',
        indexDescription: 'Registro de Governança do Córtex & Assinaturas Formais',
        indexStatus: 'ASSINADO DIGITALMENTE', indexColor: '#16A34A',
        body: [this.buildValidationAndSignaturesSection(data.book, data.issues, data.report)]
      }
    );

    if (data.isPreliminary) {
      sections.push({
        title: 'APÊNDICE: PENDÊNCIAS E APONTAMENTOS TÉCNICOS',
        indexDescription: 'Relação de Pendências Técnicas, Itens Faltantes e Bloqueadores Regulatórios',
        indexStatus: 'STATUS PRELIMINAR',
        indexColor: '#DC2626',
        body: [this.buildPendencyAppendixSection(data.issues, data.items)]
      });
    }

    return sections;
  }

  /** Numera título e índice a partir da mesma lista — sem número fixo no código. */
  private renderSectionRange(sections: BookPdfSection[], from: number, to: number): Content[] {
    const content: Content[] = [];
    for (let index = from; index < to; index++) {
      const section = sections[index]!;
      const number = String(index + 1).padStart(2, '0');
      content.push(this.buildSectionTitle(number, section.title));
      if (section.body.length === 0 && section.title.startsWith('ÍNDICE')) {
        content.push(this.buildIndexSection(sections));
        content.push({ text: '', pageBreak: 'after' });
        continue;
      }
      content.push(...section.body);
    }
    return content;
  }

  /**
   * Monta o `content:` do documento. Quando existe LISTA DE LINHAS, ela entra
   * num node `section` nativo do pdfmake (A3 paisagem, comando "A3 sem
   * compressão de texto") — o resto do Book (antes e depois) permanece A4
   * retrato, cada trecho na sua própria `section` para garantir a reversão de
   * tamanho de página (verificado: sem a `section` de volta, o pdfmake
   * mantém o tamanho da seção anterior nas páginas seguintes). Sem Lista de
   * Linhas, o Book inteiro sai sem nenhum node `section` — comportamento
   * idêntico ao de antes desta mudança (zero regressão).
   */
  private buildContentWithSections(sections: BookPdfSection[], lineListLayout: LineListLayoutResult | null): Content[] {
    const listaIndex = sections.findIndex(s => s.title === 'LISTA DE LINHAS');
    if (listaIndex === -1) {
      return this.renderSectionRange(sections, 0, sections.length);
    }

    // Margem lateral da seção A3 vem do autoajuste (comando "autoajuste
    // inteligente", item 7) — 8mm por padrão, reduzida para 5mm só se o
    // autoajuste decidiu que era necessário para caber. Sem Linhas ou em
    // falha de layout, usa a margem padrão (não há tabela para dimensionar).
    const sideMarginPt = lineListLayout && lineListLayout.ok ? lineListLayout.sideMarginPt : 22.68;
    const content: Content[] = [
      ...this.renderSectionRange(sections, 0, listaIndex),
      {
        section: this.renderSectionRange(sections, listaIndex, listaIndex + 1),
        pageSize: LINE_LIST_PAGE_SIZE,
        pageOrientation: LINE_LIST_PAGE_ORIENTATION,
        pageMargins: lineListPageMargins(sideMarginPt)
      }
    ];
    if (listaIndex + 1 < sections.length) {
      content.push({
        section: this.renderSectionRange(sections, listaIndex + 1, sections.length),
        pageSize: BOOK_PAGE_SIZE,
        pageOrientation: BOOK_PAGE_ORIENTATION,
        pageMargins: BOOK_PAGE_MARGINS
      });
    }
    return content;
  }

  private buildCoverPage(book: any, equipment: any, inspection: any, report: any, isPreliminary: boolean | undefined, visual: DocumentVisual | null): Content {
    // STUDIO INSPECT IA — logo publicada (imagem) substitui o bloco de texto
    // institucional da capa; sem logo, o texto de sempre é tingido pela cor
    // do cabeçalho do tema.
    const heroContent: Content[] = visual?.logoImage
      ? [{ image: visual.logoImage, fit: [180, 40], alignment: 'center', margin: [0, 12, 0, 12] } as Content]
      : [
          // STUDIO INSPECT IA — este texto senta sobre o fundo escuro fixo
          // abaixo (faixa gráfica estrutural, não tingida pelo tema: o
          // `headerText` do tema é calibrado para fundo claro do cabeçalho/
          // corpo do documento e ficaria ilegível aqui se herdado).
          { text: visual?.logoText || 'INSPECT IA', color: '#60A5FA', fontSize: 14, bold: true, alignment: 'center', margin: [0, 12, 0, 2] },
          { text: 'SISTEMA INTEGRADO DE INTEGRIDADE & ENGENHARIA DE CONFIABILIDADE', color: '#94A3B8', fontSize: 8, alignment: 'center', margin: [0, 0, 0, 12] }
        ];
    const coverElements: Content[] = [
      { text: '', margin: [0, 10, 0, 0] },
      {
        table: {
          widths: ['100%'],
          body: [
            [{
              fillColor: '#1E293B',
              stack: heroContent
            }]
          ]
        },
        layout: 'noBorders'
      },
      { text: '', margin: [0, 14, 0, 0] },
      { text: 'BOOK DE INSPEÇÃO TÉCNICA', style: 'coverMainTitle' },
      { text: 'DOSSIÊ CONSOLIDADO DE INTEGRIDADE E CONFORMIDADE NR-13', style: 'coverSubtitle' },
      {
        canvas: [{ type: 'line', x1: 50, y1: 0, x2: 473, y2: 0, lineWidth: 1.5, lineColor: bookColor('sectionNumberFill', BOOK_COLORS.accent) }],
        margin: [0, 0, 0, 14]
      }
    ];

    if (isPreliminary) {
      coverElements.push({
        table: {
          widths: ['100%'],
          body: [
            [{
              fillColor: '#FEF2F2',
              stack: [
                { text: '⚠️ STATUS: PRELIMINAR / INCOMPLETO', color: '#DC2626', fontSize: 9.5, bold: true, alignment: 'center', margin: [0, 5, 0, 2] },
                { text: 'Este documento é uma prévia técnica consolidada em elaboração. Não possui validade como emissão final conclusiva de inspeção NR-13.', color: '#991B1B', fontSize: 7, alignment: 'center', margin: [0, 0, 0, 5] }
              ]
            }]
          ]
        },
        layout: {
          hLineWidth: () => 0.75,
          vLineWidth: () => 0.75,
          hLineColor: () => '#F87171',
          vLineColor: () => '#F87171',
        },
        margin: [0, 0, 0, 12]
      });
    }

    coverElements.push({
      table: {
        widths: ['34%', '66%'],
        body: [
          [{ text: 'NÚMERO DO BOOK:', style: 'tableHeader', noWrap: true }, { text: book.bookNumber || `BOOK-${new Date().getFullYear()}`, style: 'boldCell' }],
          [{ text: 'REVISÃO DO BOOK:', style: 'tableHeader', noWrap: true }, { text: `Rev. ${book.revision || '00'}`, style: 'boldCell' }],
          [{ text: 'TAG DO EQUIPAMENTO:', style: 'tableHeader', noWrap: true }, { text: equipment.tag || equipment.name || '-', style: 'boldCell' }],
          [{ text: 'NOME DO EQUIPAMENTO:', style: 'tableHeader', noWrap: true }, { text: equipment.name || '-', style: 'tableCell' }],
          [{ text: 'TIPO DO EQUIPAMENTO:', style: 'tableHeader', noWrap: true }, { text: equipment.equipmentType || '-', style: 'tableCell' }],
          [{ text: 'ORDEM DE SERVIÇO (OS):', style: 'tableHeader', noWrap: true }, { text: inspection.workOrderId || '-', style: 'tableCell' }],
          [{ text: 'CLIENTE / UNIDADE:', style: 'tableHeader', noWrap: true }, { text: book.clientName || equipment.clientName || inspection.clientName || 'Cliente Corporativo', style: 'tableCell' }],
          [{ text: 'TIPO DE INSPEÇÃO:', style: 'tableHeader', noWrap: true }, { text: `${inspection.inspectionType || '-'} • ${inspection.nature || '-'}`, style: 'tableCell' }],
          [{ text: 'DATA DA INSPEÇÃO:', style: 'tableHeader', noWrap: true }, { text: inspection.startDate ? new Date(inspection.startDate).toLocaleDateString('pt-BR') : new Date().toLocaleDateString('pt-BR'), style: 'tableCell', noWrap: true }],
          [{ text: 'DATA DE EMISSÃO DO BOOK:', style: 'tableHeader', noWrap: true }, { text: new Date().toLocaleDateString('pt-BR'), style: 'tableCell', noWrap: true }],
          [{ text: 'STATUS DOCUMENTAL:', style: 'tableHeader', noWrap: true }, { text: isPreliminary ? (book.status === 'ISSUED' ? 'EMITIDO' : `${book.status || 'DRAFT'} (PRELIMINAR)`) : (book.status || 'READY_FOR_ISSUE'), style: 'boldCell', color: isPreliminary ? '#DC2626' : '#16A34A', noWrap: true }],
          [{ text: 'PLH RESPONSÁVEL:', style: 'tableHeader', noWrap: true }, { text: report.plhSignerName || report.engineerPlhName || 'Engenheiro PLH Responsável', style: 'boldCell' }],
        ]
      },
      layout: {
        hLineWidth: () => 0.5,
        vLineWidth: () => 0.5,
        hLineColor: () => bookColor('border', BOOK_COLORS.border),
        vLineColor: () => bookColor('border', BOOK_COLORS.border),
        paddingLeft: () => 8,
        paddingRight: () => 8,
        paddingTop: () => 5,
        paddingBottom: () => 5,
      }
    });

    return {
      stack: coverElements
    };
  }

  private buildSectionTitle(number: string, title: string): Content {
    const bandFill = bookColor('sectionTitleFill', BOOK_COLORS.sectionBandFill);
    const bar = bookColor('sectionNumberFill', BOOK_COLORS.accent);
    return {
      table: {
        widths: ['100%'],
        body: [
          [{
            fillColor: bandFill,
            stack: [
              { text: `${number}. ${title}`, style: 'sectionHeading' }
            ]
          }]
        ]
      },
      layout: {
        hLineWidth: () => 0.5,
        vLineWidth: (i) => (i === 0 ? 3 : 0.5),
        hLineColor: () => bookColor('border', BOOK_COLORS.border),
        vLineColor: (i) => (i === 0 ? bar : bookColor('border', BOOK_COLORS.border)),
        paddingLeft: () => 8,
        paddingRight: () => 8,
        paddingTop: () => 4,
        paddingBottom: () => 4,
      },
      margin: [0, 8, 0, 6]
    };
  }

  private buildDocumentMatrixTable(items: any[]): Content {
    const rows: TableCell[][] = [
      [
        { text: 'ITEM', style: 'tableHeader', alignment: 'center', noWrap: true },
        { text: 'DOCUMENTO', style: 'tableHeader' },
        { text: 'CATEGORIA', style: 'tableHeader', noWrap: true },
        { text: 'APLICABILIDADE', style: 'tableHeader', noWrap: true },
        { text: 'STATUS', style: 'tableHeader', noWrap: true },
        { text: 'REV.', style: 'tableHeader', alignment: 'center', noWrap: true },
        { text: 'OBSERVAÇÃO / JUSTIFICATIVA N/A', style: 'tableHeader' },
      ]
    ];

    items.forEach((item, idx) => {
      const isNA = item.applicability === 'NOT_APPLICABLE';
      const statusColor = item.status === 'AVAILABLE' ? '#16A34A' : (item.status === 'PENDING' ? '#DC2626' : '#D97706');
      rows.push([
        { text: String(idx + 1).padStart(2, '0'), style: 'tableCell', alignment: 'center', noWrap: true },
        { text: item.title, style: 'boldCell' },
        { text: item.category, style: 'tableCell', noWrap: true },
        { text: isNA ? 'NÃO APLICÁVEL' : 'APLICÁVEL', style: 'tableCell', color: isNA ? '#94A3B8' : '#0F172A', noWrap: true },
        { text: item.status, style: 'tableCell', color: statusColor, noWrap: true },
        { text: item.revision || '00', style: 'tableCell', alignment: 'center', noWrap: true },
        { text: isNA ? (item.notApplicableReason || 'N/A') : (item.fileName || 'Disponível no sistema'), style: 'tableCell' },
      ]);
    });

    return {
      table: {
        widths: ['5%', '27%', '12%', '14%', '12%', '6%', '24%'],
        body: rows,
      },
      layout: {
        hLineWidth: () => 0.5,
        vLineWidth: () => 0.5,
        hLineColor: () => bookColor('border', BOOK_COLORS.gridBorder),
        vLineColor: () => bookColor('border', BOOK_COLORS.gridBorder),
        paddingLeft: () => 4,
        paddingRight: () => 4,
        paddingTop: () => 3,
        paddingBottom: () => 3,
      }
    };
  }

  /** Índice dinâmico: uma linha por seção realmente emitida, na mesma numeração do corpo. */
  private buildIndexSection(sections: BookPdfSection[]): Content {
    const indexRows: TableCell[][] = [
      [
        { text: 'SEÇÃO', style: 'tableHeader', noWrap: true },
        { text: 'DESCRIÇÃO DO CONTEÚDO', style: 'tableHeader' },
        { text: 'STATUS NO BOOK', style: 'tableHeader', noWrap: true },
      ],
      ...sections.map((section, index) => ([
        { text: `SEÇÃO ${String(index + 1).padStart(2, '0')}`, style: 'boldCell', noWrap: true },
        { text: section.indexDescription, style: 'tableCell' },
        { text: section.indexStatus, style: 'tableCell', noWrap: true, ...(section.indexColor ? { color: section.indexColor } : {}) },
      ] as TableCell[]))
    ];

    return {
      table: {
        widths: ['15%', '65%', '20%'],
        body: indexRows,
      },
      layout: {
        hLineWidth: () => 0.5,
        vLineWidth: () => 0.5,
        hLineColor: () => bookColor('border', BOOK_COLORS.gridBorder),
        vLineColor: () => bookColor('border', BOOK_COLORS.gridBorder),
        paddingLeft: () => 6,
        paddingRight: () => 6,
        paddingTop: () => 4,
        paddingBottom: () => 4,
      }
    };
  }

  /**
   * LISTA DE LINHAS do Sistema de Tubulação — mesmo modelo consumido pelo
   * Preview (PREVIEW = PDF). A tabela repete o cabeçalho a cada página
   * (`headerRows`) e nenhum registro é cortado no meio (`dontBreakRows`).
   */
  private buildLineListSection(list: GeneratedLineList, layout: LineListLayoutResult | null, book: any, equipment: any, inspection: any): Content[] {
    const identification: Content = {
      table: {
        widths: ['25%', '25%', '25%', '25%'],
        body: [
          [{ text: 'SISTEMA DE TUBULAÇÃO:', style: 'tableHeader' }, { text: formatReportValue(list.systemTag || equipment?.tag), style: 'boldCell' },
           { text: 'DESCRIÇÃO / SERVIÇO:', style: 'tableHeader' }, { text: formatReportValue(list.systemDescription), style: 'tableCell' }],
          [{ text: 'ORDEM DE SERVIÇO (OS):', style: 'tableHeader' }, { text: formatReportValue(inspection?.osNumber ?? inspection?.workOrderId), style: 'tableCell' },
           { text: 'UNIDADE / ÁREA:', style: 'tableHeader' }, { text: formatReportValue(equipment?.unitArea ?? equipment?.unitAreaId), style: 'tableCell' }],
          [{ text: 'BOOK:', style: 'tableHeader' }, { text: formatReportValue(book?.bookNumber), style: 'tableCell' },
           { text: 'REVISÃO:', style: 'tableHeader' }, { text: formatReportValue(book?.revision), style: 'tableCell' }],
          [{ text: 'DATA:', style: 'tableHeader' }, { text: new Date(list.generatedAt).toLocaleDateString('pt-BR'), style: 'tableCell' },
           { text: 'TOTAL DE LINHAS DO SISTEMA:', style: 'tableHeader' }, { text: String(list.totalLines), style: 'boldCell' }],
        ]
      },
      layout: {
        hLineWidth: () => 0.5, vLineWidth: () => 0.5,
        hLineColor: () => bookColor('border', BOOK_COLORS.gridBorder), vLineColor: () => bookColor('border', BOOK_COLORS.gridBorder),
        paddingLeft: () => 6, paddingRight: () => 6, paddingTop: () => 4, paddingBottom: () => 4,
      },
      margin: [0, 0, 0, 10]
    };

    if (list.totalLines === 0) {
      return [
        identification,
        { text: 'SISTEMA SEM LINHAS CADASTRADAS — o Sistema de Tubulação não possui Linhas vinculadas no Cadastro de Equipamentos.', style: 'tableCell', margin: [0, 4, 0, 0] }
      ];
    }

    if (!layout || !layout.ok) {
      return [
        identification,
        buildLineListFailureBlock(
          layout && !layout.ok
            ? layout
            : { ok: false, message: 'Não foi possível gerar a Lista de Linhas mantendo os critérios mínimos de legibilidade e integridade dos dados.', offendingColumns: [], shortfallPt: 0 }
        )
      ];
    }

    const content: Content[] = [identification, buildLineListTable(list, layout.widths)];
    const references = buildLineListReferencesBlock(list);
    if (references) content.push(references);
    return content;
  }

  private buildInspectionDataSection(equipment: any, inspection: any, report: any): Content {
    const props = equipment.customProperties || {};
    const pmta = props.pmta || props.designPressure || equipment.designPressure || '-';
    const pth = props.testPressure || equipment.testPressure || '-';
    const temp = props.designTemperature || equipment.designTemperature || '-';
    const vol = props.volume || equipment.volume || '-';
    const fluido = props.fluid || equipment.service || '-';
    const norma = props.constructionCode || equipment.constructionCode || '-';
    const material = props.material || equipment.material || '-';
    const margem = props.corrosionAllowance || equipment.corrosionAllowance || '-';
    const categoria = equipment.category || props.category || 'Não definida';

    return {
      table: {
        widths: ['24%', '26%', '24%', '26%'],
        body: [
          [{ text: 'TAG DO EQUIPAMENTO:', style: 'tableHeader', noWrap: true }, { text: equipment.tag || '-', style: 'boldCell', noWrap: true }, { text: 'CATEGORIA NR-13:', style: 'tableHeader', noWrap: true }, { text: categoria, style: 'boldCell', noWrap: true }],
          [{ text: 'PRESSÃO PROJETO (PMTA):', style: 'tableHeader', noWrap: true }, { text: pmta, style: 'boldCell', color: '#16A34A', noWrap: true }, { text: 'PRESSÃO TESTE (PTH):', style: 'tableHeader', noWrap: true }, { text: pth, style: 'tableCell', noWrap: true }],
          [{ text: 'TEMPERATURA PROJETO:', style: 'tableHeader', noWrap: true }, { text: temp, style: 'tableCell', noWrap: true }, { text: 'VOLUME INTERNO:', style: 'tableHeader', noWrap: true }, { text: vol, style: 'tableCell', noWrap: true }],
          [{ text: 'FLUIDO DE TRABALHO:', style: 'tableHeader', noWrap: true }, { text: fluido, style: 'tableCell', noWrap: true }, { text: 'CÓDIGO CONSTRUÇÃO:', style: 'tableHeader', noWrap: true }, { text: norma, style: 'tableCell', noWrap: true }],
          [{ text: 'MATERIAL CASCO/TAMPOS:', style: 'tableHeader', noWrap: true }, { text: material, style: 'tableCell', noWrap: true }, { text: 'SOBREESPESSURA CORROSÃO:', style: 'tableHeader', noWrap: true }, { text: margem, style: 'tableCell', noWrap: true }],
        ]
      },
      layout: {
        hLineWidth: () => 0.5,
        vLineWidth: () => 0.5,
        hLineColor: () => bookColor('border', BOOK_COLORS.gridBorder),
        vLineColor: () => bookColor('border', BOOK_COLORS.gridBorder),
        paddingLeft: () => 6,
        paddingRight: () => 6,
        paddingTop: () => 3.5,
        paddingBottom: () => 3.5,
      }
    };
  }

  private buildChecklistSummarySection(checklist: any): Content {
    const items = Array.isArray(checklist) ? checklist : (checklist?.items || []);
    const tableBody: TableCell[][] = [
      [{ text: 'COMPONENTE AVALIADO', style: 'tableHeader' }, { text: 'RESULTADO', style: 'tableHeader', noWrap: true }, { text: 'OBSERVAÇÕES TÉCNICAS', style: 'tableHeader' }]
    ];

    if (items.length > 0) {
      items.forEach((item: any) => {
        const isNC = item.status === 'REJECTED';
        const isRec = item.status === 'WITH_RECOMMENDATION' || (item.recommendations && item.recommendations.length > 0);
        const isNa = item.status === 'NOT_APPLICABLE';
        const color = isNC ? '#DC2626' : (isRec ? '#D97706' : (isNa ? '#64748B' : (item.status === 'APPROVED' ? '#16A34A' : '#D97706')));
        const statusLabel = isNC ? 'REPROVADO' : (isRec ? 'C/ RECOMENDAÇÃO' : (item.status === 'APPROVED' ? 'APROVADO' : (isNa ? 'NÃO APLICÁVEL' : 'PENDÊNCIA DE PREENCHIMENTO')));

        tableBody.push([
          { text: item.name || item.component || 'Componente', style: 'boldCell' },
          { text: statusLabel, style: 'tableCell', color, noWrap: true },
          { text: item.technicalComment || item.notes || '-', style: 'tableCell' }
        ]);
      });
    } else {
      tableBody.push([
        { text: '-', style: 'tableCell' },
        { text: '-', style: 'tableCell', noWrap: true },
        { text: 'Nenhum item de checklist registrado para esta inspeção.', style: 'tableCell' }
      ]);
    }

    return {
      stack: [
        {
          text: 'O Checklist Visual NR-13 foi executado e abrangeu os componentes aplicáveis do equipamento conforme registrado.',
          style: 'tableCell',
          margin: [0, 0, 0, 6]
        },
        {
          table: {
            widths: ['32%', '22%', '46%'],
            body: tableBody
          },
          layout: {
            hLineWidth: () => 0.5,
            vLineWidth: () => 0.5,
            hLineColor: () => bookColor('border', BOOK_COLORS.gridBorder),
            vLineColor: () => bookColor('border', BOOK_COLORS.gridBorder),
            paddingLeft: () => 6,
            paddingRight: () => 6,
            paddingTop: () => 3.5,
            paddingBottom: () => 3.5,
          }
        }
      ]
    };
  }

  private buildRiSummarySection(report: any, equipment: any): Content {
    return {
      stack: [
        {
          text: report.executiveSummary || report.inspectionObservations || 'O equipamento foi submetido a inspeção conforme preconizado pela norma regulamentadora NR-13.',
          style: 'tableCell',
          margin: [0, 0, 8, 8]
        },
        { text: 'PARECER TÉCNICO CONCLUSIVO DO PLH:', style: 'tableHeader', margin: [0, 4, 0, 4] },
        {
          table: {
            widths: ['100%'],
            body: [
              [{
                fillColor: bookColor('sectionTitleFill', BOOK_COLORS.sectionBandFill),
                text: report.conclusionText || `Considerando os resultados dos exames visuais e ensaios executados, o equipamento ${equipment.tag || ''} foi inspecionado em conformidade com as diretrizes do PLH.`,
                style: 'tableCell',
                margin: [4, 4, 4, 4]
              }]
            ]
          },
          layout: {
            hLineWidth: () => 0.5,
            vLineWidth: (i) => (i === 0 ? 3 : 0.5),
            hLineColor: () => bookColor('border', BOOK_COLORS.border),
            vLineColor: (i) => (i === 0 ? '#16A34A' : bookColor('border', BOOK_COLORS.border)),
            paddingLeft: () => 8,
            paddingRight: () => 8,
            paddingTop: () => 5,
            paddingBottom: () => 5,
          }
        }
      ]
    };
  }

  private buildRecommendationsSection(recommendations: any[]): Content {
    const recRows: TableCell[][] = [
      [
        { text: 'CÓDIGO', style: 'tableHeader', alignment: 'center', noWrap: true },
        { text: 'DISCIPLINA', style: 'tableHeader', noWrap: true },
        { text: 'CRITICIDADE', style: 'tableHeader', noWrap: true },
        { text: 'DESCRIÇÃO DA AÇÃO RECOMENDADA', style: 'tableHeader' },
        { text: 'PRAZO', style: 'tableHeader', noWrap: true },
        { text: 'STATUS', style: 'tableHeader', noWrap: true },
      ]
    ];

    const getCriticalityColor = (crit: string | undefined): string => {
      const c = (crit || '').toUpperCase();
      if (c.includes('CRÍTICA') || c.includes('CRITICAL') || c.includes('ALTA') || c.includes('HIGH') || c.includes('BLOCKER')) return '#DC2626';
      if (c.includes('MÉDIA') || c.includes('MODERATE') || c.includes('MEDIUM') || c.includes('MEDIO')) return '#D97706';
      return '#2563EB';
    };

    const getStatusColor = (status: string | undefined): string => {
      const s = (status || '').toUpperCase();
      if (s.includes('CONCLUÍDA') || s.includes('RESOLVIDA') || s.includes('EXECUTADA') || s.includes('DONE')) return '#16A34A';
      if (s.includes('ANDAMENTO') || s.includes('IN_PROGRESS')) return '#2563EB';
      return '#D97706';
    };

    if (!recommendations || recommendations.length === 0) {
      recRows.push([
        { text: '-', style: 'tableCell', alignment: 'center', noWrap: true },
        { text: '-', style: 'tableCell', noWrap: true },
        { text: '-', style: 'tableCell', noWrap: true },
        { text: 'Nenhuma recomendação técnica cadastrada.', style: 'tableCell' },
        { text: '-', style: 'tableCell', noWrap: true },
        { text: '-', style: 'tableCell', noWrap: true },
      ]);
    } else {
      recommendations.forEach(r => {
        recRows.push([
          { text: r.code || r.id?.slice(0, 6) || 'REC', style: 'boldCell', alignment: 'center', noWrap: true },
          { text: r.discipline || 'Mecânica', style: 'tableCell', noWrap: true },
          { text: r.criticality || 'MÉDIA', style: 'boldCell', color: getCriticalityColor(r.criticality), noWrap: true },
          { text: r.description || r.scope || 'Recomendação técnica preventiva.', style: 'tableCell' },
          { text: r.deadline || r.executeIn || 'Próxima Parada', style: 'tableCell', noWrap: true },
          { text: r.status || 'ABERTA', style: 'tableCell', color: getStatusColor(r.status), noWrap: true },
        ]);
      });
    }

    return {
      table: {
        widths: ['10%', '16%', '14%', '38%', '11%', '11%'],
        body: recRows,
      },
      layout: {
        hLineWidth: () => 0.5,
        vLineWidth: () => 0.5,
        hLineColor: () => bookColor('border', BOOK_COLORS.gridBorder),
        vLineColor: () => bookColor('border', BOOK_COLORS.gridBorder),
        paddingLeft: () => 4,
        paddingRight: () => 4,
        paddingTop: () => 3.5,
        paddingBottom: () => 3.5,
      }
    };
  }

  private buildAttachedDocsSection(items: any[]): Content {
    const attachedItems = items.filter(i => ['DRAWINGS', 'LINE_LIST', 'ISOMETRICS', 'PIT', 'FLOW_DIAGRAM', 'MAINTENANCE_DOCS'].includes(i.documentType));
    const rows: TableCell[][] = [
      [
        { text: 'TIPO DE DOCUMENTO', style: 'tableHeader', noWrap: true },
        { text: 'TÍTULO / ARQUIVO', style: 'tableHeader' },
        { text: 'APLICABILIDADE', style: 'tableHeader', noWrap: true },
        { text: 'STATUS', style: 'tableHeader', noWrap: true },
      ]
    ];

    attachedItems.forEach(item => {
      rows.push([
        { text: item.category, style: 'boldCell', noWrap: true },
        { text: item.applicability === 'NOT_APPLICABLE' ? `Não Aplicável (${item.notApplicableReason || ''})` : (item.fileName || item.title), style: 'tableCell' },
        { text: item.applicability, style: 'tableCell', noWrap: true },
        { text: item.status, style: 'tableCell', color: item.status === 'AVAILABLE' ? '#16A34A' : '#94A3B8', noWrap: true },
      ]);
    });

    return {
      table: {
        widths: ['25%', '45%', '15%', '15%'],
        body: rows,
      },
      layout: {
        hLineWidth: () => 0.5,
        vLineWidth: () => 0.5,
        hLineColor: () => bookColor('border', BOOK_COLORS.gridBorder),
        vLineColor: () => bookColor('border', BOOK_COLORS.gridBorder),
        paddingLeft: () => 6,
        paddingRight: () => 6,
        paddingTop: () => 4,
        paddingBottom: () => 4,
      }
    };
  }

  private buildValidationAndSignaturesSection(book: any, issues: any[], report: any): Content {
    const openBlockers = issues.filter(i => i.severity === 'BLOCKER' && i.status === 'OPEN');
    const overrides = issues.filter(i => i.status === 'ENGINEER_OVERRIDE');

    return {
      stack: [
        {
          text: 'Validação Automática e Governança Técnica do Córtex:',
          style: 'tableHeader',
          margin: [0, 0, 0, 4]
        },
        {
          table: {
            widths: ['40%', '60%'],
            body: [
              [{ text: 'Status da Validação do Book:', style: 'tableHeader', noWrap: true }, { text: openBlockers.length === 0 ? 'VALIDAÇÃO AUTOMATIZADA CONCLUÍDA' : 'EMISSÃO COM PENDÊNCIAS', style: 'boldCell', color: openBlockers.length === 0 ? '#16A34A' : '#DC2626', noWrap: true }],
              [{ text: 'Bloqueadores Abertos (Blockers):', style: 'tableHeader', noWrap: true }, { text: String(openBlockers.length), style: 'boldCell', noWrap: true }],
              [{ text: 'Liberações Formais com Aval do Engenheiro (Overrides):', style: 'tableHeader' }, { text: `${overrides.length} pendência(s) com justificativa técnica registrada.`, style: 'tableCell' }],
            ]
          },
          layout: {
            hLineWidth: () => 0.5,
            vLineWidth: () => 0.5,
            hLineColor: () => bookColor('border', BOOK_COLORS.gridBorder),
            vLineColor: () => bookColor('border', BOOK_COLORS.gridBorder),
            paddingLeft: () => 6,
            paddingRight: () => 6,
            paddingTop: () => 3.5,
            paddingBottom: () => 3.5,
          },
          margin: [0, 0, 0, 10]
        },
        {
          table: {
            widths: ['100%'],
            body: [
              [{
                fillColor: bookColor('sectionTitleFill', BOOK_COLORS.sectionBandFill),
                stack: [
                  { text: 'AUTENTICIDADE CRIPTOGRÁFICA & CONFORMIDADE DIGITAL (NR-13 / ICP-BRASIL)', style: 'tableHeader', color: bookColor('headerText', BOOK_COLORS.accent), margin: [0, 0, 0, 2] },
                  { text: 'Este documento técnico possui integridade criptográfica verificável. As assinaturas digitais abaixo apostas cumprem integralmente as exigências da Portaria MTP nº 4.219 (NR-13 item 13.5.1) e MP nº 2.200-2/2001, garantindo autoria, autenticidade e validade jurídica plena em todo o território nacional.', fontSize: 7, color: '#475569' }
                ]
              }]
            ]
          },
          layout: {
            hLineWidth: () => 0.5,
            vLineWidth: () => 0.5,
            hLineColor: () => bookColor('border', BOOK_COLORS.gridBorder),
            vLineColor: () => bookColor('border', BOOK_COLORS.gridBorder),
            paddingLeft: () => 6,
            paddingRight: () => 6,
            paddingTop: () => 4,
            paddingBottom: () => 4,
          },
          margin: [0, 0, 0, 16]
        },
        {
          columns: [
            {
              stack: [
                { text: '________________________________________', alignment: 'center', color: '#64748B' },
                { text: report.inspectorSignerName || 'Inspetor de Equipamentos Qualificado', bold: true, fontSize: 8, alignment: 'center', margin: [0, 2, 0, 0] },
                { text: 'Execução e Vistoria Técnica', fontSize: 7, color: '#64748B', alignment: 'center' }
              ],
              width: '50%'
            },
            {
              stack: [
                { text: '________________________________________', alignment: 'center', color: '#64748B' },
                { text: report.plhSignerName || 'Engenheiro Mecânico (PLH - NR-13)', bold: true, fontSize: 8, alignment: 'center', margin: [0, 2, 0, 0] },
                { text: 'Responsável Técnico / Aprovação Final', fontSize: 7, color: '#64748B', alignment: 'center' }
              ],
              width: '50%'
            }
          ]
        }
      ]
    };
  }

  private buildPendencyAppendixSection(issues: any[], items: any[]): Content {
    const openIssues = issues.filter(i => i.status === 'OPEN' || !i.status);
    const missingDocs = items.filter(i => i.applicability === 'APPLICABLE' && i.status !== 'AVAILABLE');

    const issueRows: TableCell[][] = [
      [
        { text: 'ITEM', style: 'tableHeader', alignment: 'center', noWrap: true },
        { text: 'TIPO / SEVERIDADE', style: 'tableHeader', noWrap: true },
        { text: 'DESCRIÇÃO DO APONTAMENTO / PENDÊNCIA', style: 'tableHeader' },
        { text: 'IMPACTO REGULATÓRIO / AÇÃO REQUERIDA', style: 'tableHeader' },
      ]
    ];

    if (openIssues.length > 0) {
      openIssues.forEach((issue, idx) => {
        const isBlocker = issue.severity === 'BLOCKER' || issue.severity === 'CRITICAL';
        issueRows.push([
          { text: String(idx + 1).padStart(2, '0'), style: 'tableCell', alignment: 'center', noWrap: true },
          { text: `${issue.code || issue.severity || 'PENDÊNCIA'}`, style: 'boldCell', color: isBlocker ? '#DC2626' : '#D97706', noWrap: true },
          { text: issue.description || issue.message || issue.title || '-', style: 'tableCell' },
          { text: issue.actionRequired || issue.recommendation || (isBlocker ? 'Bloqueia emissão final até resolução ou justificativa técnica' : 'Requer acompanhamento'), style: 'tableCell' },
        ]);
      });
    } else {
      issueRows.push([
        { text: '-', style: 'tableCell', alignment: 'center', noWrap: true },
        { text: 'NENHUM', style: 'tableCell', noWrap: true },
        { text: 'Nenhum apontamento bloqueador aberto no Córtex.', style: 'tableCell' },
        { text: 'Conformidade dos itens auditados.', style: 'tableCell' },
      ]);
    }

    const missingDocRows: TableCell[][] = [
      [
        { text: 'ITEM', style: 'tableHeader', alignment: 'center', noWrap: true },
        { text: 'DOCUMENTO REQUERIDO', style: 'tableHeader' },
        { text: 'CATEGORIA', style: 'tableHeader', noWrap: true },
        { text: 'STATUS ATUAL', style: 'tableHeader', noWrap: true },
      ]
    ];

    if (missingDocs.length > 0) {
      missingDocs.forEach((doc, idx) => {
        missingDocRows.push([
          { text: String(idx + 1).padStart(2, '0'), style: 'tableCell', alignment: 'center', noWrap: true },
          { text: doc.title, style: 'boldCell' },
          { text: doc.category, style: 'tableCell', noWrap: true },
          { text: doc.status === 'PENDING' ? 'PENDENTE DE UPLOAD / ELABORAÇÃO' : (doc.status || 'NÃO DISPONÍVEL'), style: 'tableCell', color: '#DC2626', noWrap: true },
        ]);
      });
    } else {
      missingDocRows.push([
        { text: '-', style: 'tableCell', alignment: 'center', noWrap: true },
        { text: 'Todos os documentos aplicáveis constam como disponíveis no Book.', style: 'tableCell' },
        { text: '-', style: 'tableCell', noWrap: true },
        { text: 'COMPLETO', style: 'tableCell', color: '#16A34A', noWrap: true },
      ]);
    }

    return {
      stack: [
        {
          text: 'Relação de Não-Conformidades e Bloqueadores Abertos:',
          style: 'tableHeader',
          margin: [0, 0, 0, 4]
        },
        {
          table: {
            widths: ['8%', '22%', '40%', '30%'],
            body: issueRows,
          },
          layout: {
            hLineWidth: () => 0.5,
            vLineWidth: () => 0.5,
            hLineColor: () => bookColor('border', BOOK_COLORS.gridBorder),
            vLineColor: () => bookColor('border', BOOK_COLORS.gridBorder),
            paddingLeft: () => 4,
            paddingRight: () => 4,
            paddingTop: () => 3.5,
            paddingBottom: () => 3.5,
          },
          margin: [0, 0, 0, 12]
        },
        {
          text: 'Documentos Aplicáveis com Pendência de Disponibilização:',
          style: 'tableHeader',
          margin: [0, 0, 0, 4]
        },
        {
          table: {
            widths: ['8%', '37%', '25%', '30%'],
            body: missingDocRows,
          },
          layout: {
            hLineWidth: () => 0.5,
            vLineWidth: () => 0.5,
            hLineColor: () => bookColor('border', BOOK_COLORS.gridBorder),
            vLineColor: () => bookColor('border', BOOK_COLORS.gridBorder),
            paddingLeft: () => 4,
            paddingRight: () => 4,
            paddingTop: () => 3.5,
            paddingBottom: () => 3.5,
          }
        }
      ]
    };
  }
}

export const inspectionBookBuilder = new InspectionBookBuilder();
documentEngine.registerBuilder('INSPECTION_BOOK', inspectionBookBuilder);
